import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DisplayFormData, DisplayData } from '../../services/displayService';

/**
 * DisplayForm Component
 * 
 * Simplified form for creating and configuring display devices
 * Focuses on MVP-required fields only: name, custom name, and location
 */

interface DisplayFormProps {
  isEdit?: boolean;
  initialData?: DisplayData;
  displayId?: string;
  clientId?: string; // For initial registration
  onSubmit: (formData: DisplayFormData) => Promise<void>;
  clientDisplayName?: string;
  onCancel?: () => void;
}

const DisplayForm: React.FC<DisplayFormProps> = ({
  isEdit = false,
  initialData,
  displayId,
  clientId,
  onSubmit,
  clientDisplayName,
  onCancel,
}) => {
  const navigate = useNavigate();

  // Initialize form data with clientId from props - simplified to only include MVP fields
  const [formData, setFormData] = useState<DisplayFormData>(() => {
    if (initialData) {
      // Logic for edit mode (initialData present)
      return {
        name: initialData.name,
        customName: initialData.customName || '',
        location: {
          zipcode: initialData.location?.zipcode || '',
          label: initialData.location?.label || ''
        },
        clientId: initialData.client_display_id || '', // In edit, use client_display_id from data
      };
    } else {
      // For new form, ensure clientId from props is used correctly
      return {
        name: '',
        customName: '',
        location: { zipcode: '', label: '' },
        // Make sure we use the clientId prop directly, not checking for falsy
        clientId: clientId || '', 
      };
    }
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Using useEffect to update clientId in state when clientId prop changes
  // This will run on mount and whenever the clientId prop changes
  useEffect(() => {
    if (clientId && !isEdit) {
      // For new form, update clientId directly whenever prop changes
      setFormData(prev => ({ 
        ...prev, 
        clientId: clientId // Set directly without conditional checks
      }));
    }
  }, [clientId, isEdit]);

  // Secondary useEffect for initialData changes, keeping it separate for clarity
  useEffect(() => {
    if (initialData) {
      // If initialData changes (e.g. navigating between edit forms), re-initialize from it.
      setFormData({
        name: initialData.name,
        customName: initialData.customName || '',
        location: {
          zipcode: initialData.location?.zipcode || '',
          label: initialData.location?.label || ''
        },
        clientId: initialData.client_display_id || '',
      });
    }
  }, [initialData]);

  // Handle text input changes - simplified without nested configuration handling
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    // Handle nested properties for location only
    if (name.includes('.')) {
      const parts = name.split('.');
      
      if (parts.length === 2) {
        const [parent, child] = parts;
        setFormData(prev => ({
          ...prev,
          [parent]: {
            ...(prev[parent as keyof DisplayFormData] as Record<string, unknown>),
            [child]: value
          }
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    
    // Clear error when field is changed
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  // Validate form data - simplified validation for MVP fields only
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = 'Display name is required';
    }
    
    // Location validation
    if (!formData.location.zipcode.trim()) {
      newErrors['location.zipcode'] = 'Zipcode is required';
    } else if (!/^\d{5}(-\d{4})?$/.test(formData.location.zipcode)) {
      newErrors['location.zipcode'] = 'Invalid zipcode format';
    }
    
    // Client ID validation for registration (not edit)
    if (!isEdit && !formData.clientId.trim()) {
      newErrors.clientId = 'Client display ID is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission - simplified to only submit MVP data
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      setIsSubmitting(true);
      setSubmitError(null);
      
      try {
        // Submit only the core DisplayFormData without configuration
        const submissionData: DisplayFormData = {
          name: formData.name,
          customName: formData.customName,
          location: formData.location,
          clientId: formData.clientId
        };
        
        await onSubmit(submissionData);
        
        // Redirect after successful submission
        if (isEdit) {
          navigate(`/my-displays/${displayId}`);
        } else {
          navigate('/my-displays');
        }
      } catch (error) {
        console.error('Error submitting display:', error);
        setSubmitError(
          error instanceof Error 
            ? error.message 
            : 'An unexpected error occurred. Please try again.'
        );
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <div className="display-form-container">
      <h2>{isEdit ? 'Edit Display' : 'Register New Display'}</h2>
      
      {submitError && (
        <div className="alert alert-danger" role="alert">
          {submitError}
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="row g-3 needs-validation" noValidate>
        {/* Display Name */}
        <div className="col-md-12">
          <label htmlFor="name" className="form-label">Display Name <span className="text-danger">*</span></label>
          <input
            type="text"
            id="name"
            name="name"
            className={`form-control ${errors.name ? 'is-invalid' : ''}`}
            value={formData.name}
            onChange={handleChange}
            placeholder="Enter a name for this display"
            required
            data-testid="name-input"
          />
          {errors.name && <div className="invalid-feedback">{errors.name}</div>}
          <div className="form-text">This name is for your reference only</div>
        </div>
        
        {/* Custom Display Name */}
        <div className="col-md-12">
          <label htmlFor="customName" className="form-label">Custom Display Name (Optional)</label>
          <input
            type="text"
            id="customName"
            name="customName"
            className="form-control"
            value={formData.customName}
            onChange={handleChange}
            placeholder="Shown on the display itself"
          />
          <div className="form-text">This name will be shown on the display (leave blank to use default)</div>
        </div>
        
        {/* Location Section */}
        <div className="col-md-12">
          <h4>Location</h4>
          <div className="row">
            {/* Zipcode */}
            <div className="col-md-6 mb-3">
              <label htmlFor="zipcode" className="form-label">Zipcode <span className="text-danger">*</span></label>
              <input
                type="text"
                id="zipcode"
                name="location.zipcode"
                className={`form-control ${errors['location.zipcode'] ? 'is-invalid' : ''}`}
                value={formData.location.zipcode}
                onChange={handleChange}
                placeholder="e.g., 12345"
                required
                data-testid="zipcode-input"
              />
              {errors['location.zipcode'] && (
                <div className="invalid-feedback">{errors['location.zipcode']}</div>
              )}
            </div>
            
            {/* Location Label */}
            <div className="col-md-6 mb-3">
              <label htmlFor="locationLabel" className="form-label">Location Label (Optional)</label>
              <input
                type="text"
                id="locationLabel"
                name="location.label"
                className="form-control"
                value={formData.location.label}
                onChange={handleChange}
                placeholder="e.g., Main Lobby"
              />
              <div className="form-text">Descriptive label for this display's location</div>
            </div>
          </div>
        </div>
        
        {/* Hidden Client ID field - typically populated from URL parameter */}
        <div className="col-md-12">
          <input
            type="hidden"
            name="clientId"
            value={formData.clientId}
            data-testid="form-client-id"
          />
        </div>
        
        {/* Submit Button */}
        <div className="d-grid gap-2 d-md-flex justify-content-md-end">
          <button
            type="button"
            className="btn btn-secondary me-md-2"
            onClick={() => navigate(-1)}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                {isEdit ? 'Updating...' : 'Registering...'}
              </>
            ) : (
              isEdit ? 'Update Display' : 'Register Display'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default DisplayForm; 